import React from 'react';
import {
  Dropdown,
  DropdownItemProps,
  DropdownProps,
  HtmlSpanProps,
  SemanticShorthandItem,
} from 'semantic-ui-react';
import { ErrorMessage } from '../../MessageText';
import { DropdownStyling } from '../Basics/Dropdown';

type TagOption = Omit<DropdownItemProps, 'description'> & {
  description?: string | SemanticShorthandItem<HtmlSpanProps>;
};

type DropdownValueType = boolean | number | string | (boolean | number | string)[] | undefined;

export type AddableProps = Omit<DropdownProps, 'value' | 'onChange'> & {
  options: TagOption[];
  className?: string;
  value?: DropdownValueType;
  formatNewValue?: (value: string) => string;
  onError?: (error: string) => void;
  onChange?: (data: DropdownValueType) => void;
  onModified?: () => void;
};

export const AddableDropdown: React.FC<AddableProps> = ({
  options,
  formatNewValue,
  onModified,
  allowAdditions = true,
  ...props
}) => {
  const [addedOptions, setAddedOptions] = React.useState<TagOption[]>([]);
  const [error, setError] = React.useState<string>();

  /*
  DD: Can't seem to get this regex right.  Tags in different systems can be very flexible...

  const tagRegex = /^[a-zA-Z0-9_:\[\]\- ]{2,}$/;
  const onValidateTag = (value: string): void => {
    let errorMsg;
    if (!tagRegex.test(value)) {
      errorMsg = `Tag contains invalid characters.`;
    }
    if (errorMsg) {
      if (props.onError) {
        props.onError(errorMsg);
      } else {
        setError(errorMsg);
      }
      throw new Error(errorMsg);
    }
  };
  */

  const tryFormatNewValue = (values: string): string => {
    // onValidateTag(values);

    if (!formatNewValue) {
      return values;
    }
    return formatNewValue(values as string);
  };

  const onChange = (e: React.SyntheticEvent, v: DropdownProps): void => {
    e.preventDefault();
    props.onChange && props.onChange(v.value);
  };

  return (
    <>
      <Dropdown
        css={DropdownStyling}
        {...props}
        value={props.multiple ? props.value ?? [] : props.value}
        selectOnBlur={false}
        fluid
        allowAdditions={allowAdditions}
        search
        selection
        options={[...options, ...addedOptions]}
        onChange={(evt, data): void => {
          onChange(evt, data);
          onModified?.();
          if (error) {
            setError(undefined);
          }
        }}
        onAddItem={(evt, values): void => {
          try {
            const newValue = tryFormatNewValue(values.value as string).trim();
            setAddedOptions([...addedOptions, { key: newValue, text: newValue, value: newValue }]);
            onModified?.();
          } catch (e) {
            // New value not valid - don't add and don't change
            values.value = props.value;
            onChange(evt, values);
          }
        }}
      />
      {error && <ErrorMessage error={error} className='justify-start text-sm mt-1' />}
    </>
  );
};
